import '@learnforge/demo-kit/styles.css';
import './styles.css';

import * as Matter from 'matter-js';

import {
  resolveInitialTheme,
  applyTheme,
  onThemeMessage,
  mountLangPicker,
  mountTopbar,
  mountHelp,
  mountMobileDrawer,
  mountRotateNudge,
  mountThemeToggle,
  makeCollapsible,
  createSliderRow,
  createStyleRow,
  createToggleRow,
  getShotId,
  markShotMode,
  readRequestedExperiment,
  writeExperimentUrl,
  notifyHostExperiment,
  onExperimentRequest,
  type SliderRowHandle,
} from '@learnforge/demo-kit';

import { i18n, type MechKey } from './i18n';
import { SCENES, type SceneKey, type BuiltScene } from './scenes';
import { createPhysicsWorld } from './physics/world';
import { mountPalette } from './physics/palette';
import { mountActionBar } from './action-bar';
import { drawWorld, type RenderState } from './render/canvas';
import type { TraceHistory } from './render/trace';
import { pushTracePoint } from './render/trace';

// ---------- Theme ----------
applyTheme(resolveInitialTheme());
onThemeMessage((next) => applyTheme(next));
mountThemeToggle();

// ---------- Canvas ----------
const stage = document.getElementById('stage') as HTMLDivElement;
const canvas = document.createElement('canvas');
stage.appendChild(canvas);
const ctx = canvas.getContext('2d')!;

function fitCanvas() {
  const dpr = Math.min(window.devicePixelRatio, 2);
  canvas.width = Math.floor(window.innerWidth * dpr);
  canvas.height = Math.floor(window.innerHeight * dpr);
  canvas.style.width = `${window.innerWidth}px`;
  canvas.style.height = `${window.innerHeight}px`;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}
fitCanvas();
window.addEventListener('resize', () => {
  fitCanvas();
  rebuildCurrentScene();
});

function bounds() {
  return { width: window.innerWidth, height: window.innerHeight };
}

// ---------- State ----------
function isSceneKey(raw: string | null): raw is SceneKey {
  return raw !== null && Object.hasOwn(SCENES, raw);
}

// `/mechanics/<scene>` (or `?exp=<scene>`, which the platform's iframe uses)
// deep-links a scene; unknown keys fall back to the default.
const DEMO_SLUG = 'mechanics';
const requestedScene = readRequestedExperiment(DEMO_SLUG);
let currentSceneKey: SceneKey = isSceneKey(requestedScene) ? requestedScene : 'incline';
onExperimentRequest(() => notifyHostExperiment(currentSceneKey));
let dims: Record<string, number> = {};

// ---------- Chrome ----------
mountTopbar(document.getElementById('topbar') as HTMLElement, {
  eyebrowKey: 'app.eyebrow',
  i18n,
  selector: {
    options: (Object.keys(SCENES) as SceneKey[]).map((key) => ({
      value: key,
      labelKey: SCENES[key].labelKey,
    })),
    value: currentSceneKey,
    ariaLabelKey: 'panel.scene',
    onChange: (key) => {
      currentSceneKey = key;
      loadScene();
    },
  },
});
mountLangPicker(document.getElementById('langPicker') as HTMLElement, i18n);
mountHelp({ lineKeys: ['hint.line1', 'hint.line2'], i18n });
mountRotateNudge();
let playing = false;
let speedScale = 1;
let showForces = true;
let showTrace = false;
const trace: TraceHistory = { points: [] };
const forcesSeen = new Set<string>();
const hiddenForces = new Set<string>();
let lastForcesCount = -1;

// Colors per force category — must mirror the constants in physics/forces.ts so the panel
// indicator matches the arrow on the canvas.
const FORCE_COLORS: Record<string, string> = {
  'force.gravity': '#1a73e8',
  'force.normal': '#34a853',
  'force.friction': '#f9ab00',
  'force.spring': '#9c27b0',
  'force.tension': '#e91e63',
  'force.air': '#00bcd4',
  'force.applied': '#00ff00',
};
function forceNameKey(forceKey: string): MechKey {
  return forceKey.replace('force.', 'forceName.') as MechKey;
}

let bodyColor = '#c9d4e8';
let vectorOpacity = 1;
let groundColor = '#9c8d72';

const { engine, world } = createPhysicsWorld();
let built: BuiltScene | null = null;
let paletteHandle: { destroy(): void } | null = null;

// ---------- Mouse drag (Matter MouseConstraint) ----------
// MouseConstraint reads pointer events from the canvas and lets users grab any non-static
// body. The constraint's elastic stiffness is moderate so dragging feels responsive without
// fighting the rope/spring constraints in pendulum / atwood / spring-mass.
//
// Matter.Mouse defaults to scaling pointer coords by the canvas's intrinsic width / CSS
// width — which already matches our world coords since we draw in CSS pixels (via the
// ctx.setTransform(dpr) in fitCanvas). Setting `mouse.pixelRatio = 1` keeps it that way.
interface MouseConstraintOptions {
  mouse: Matter.Mouse;
  constraint?: { stiffness?: number; damping?: number };
}
const mouse = Matter.Mouse.create(canvas);
(mouse as unknown as { pixelRatio: number }).pixelRatio = 1;
const mouseConstraintOpts: MouseConstraintOptions = {
  mouse,
  constraint: { stiffness: 0.18, damping: 0.05 },
};
const mouseConstraint = Matter.MouseConstraint.create(
  engine,
  mouseConstraintOpts as unknown as Matter.IMouseConstraintDefinition,
);
Matter.World.add(world, mouseConstraint);

function grabbedBody(): Matter.Body | null {
  return (mouseConstraint as unknown as { body: Matter.Body | null }).body;
}

// ---------- Dims panel ----------
const dimsContainer = document.getElementById('dimsContainer') as HTMLElement;
const dimRowHandles: Map<string, SliderRowHandle> = new Map();

function buildDimControls() {
  dimsContainer.innerHTML = '';
  dimRowHandles.clear();
  const scene = SCENES[currentSceneKey];
  dims = {};
  for (const p of scene.params) {
    dims[p.id] = p.value;
    const row = createSliderRow({
      id: p.id,
      labelKey: p.labelKey,
      i18n,
      min: p.min,
      max: p.max,
      step: p.step,
      value: dims[p.id]!,
      onChange: (v) => {
        dims[p.id] = v;
        if (currentSceneKey === 'sandbox') {
          // Sandbox dim changes update live gravity (do not rebuild — keeps user-added bodies).
          if (p.id === 'gravityX') engine.gravity.x = v;
          if (p.id === 'gravityY') engine.gravity.y = v;
        } else {
          rebuildCurrentScene();
        }
      },
    });
    dimsContainer.appendChild(row.element);
    dimRowHandles.set(p.id, row);
  }
}

// ---------- Style panel ----------
const styleContainer = document.getElementById('styleContainer') as HTMLElement;
styleContainer.appendChild(
  createStyleRow({
    labelKey: 'style.body',
    i18n,
    color: bodyColor,
    opacity: 1,
    onColorChange: (h) => {
      bodyColor = h;
    },
    onOpacityChange: () => {},
  }).element,
);
styleContainer.appendChild(
  createStyleRow({
    labelKey: 'style.ground',
    i18n,
    color: groundColor,
    opacity: 1,
    onColorChange: (h) => {
      groundColor = h;
    },
    onOpacityChange: () => {},
  }).element,
);
styleContainer.appendChild(
  createStyleRow({
    labelKey: 'style.vector',
    i18n,
    color: '#ffffff',
    opacity: vectorOpacity,
    onColorChange: () => {},
    onOpacityChange: (v) => {
      vectorOpacity = v;
    },
  }).element,
);
styleContainer.appendChild(
  createToggleRow({
    labelKey: 'style.forces',
    i18n,
    checked: showForces,
    onChange: (v) => {
      showForces = v;
    },
  }).element,
);
styleContainer.appendChild(
  createToggleRow({
    labelKey: 'style.trace',
    i18n,
    checked: showTrace,
    onChange: (v) => {
      showTrace = v;
      trace.points = [];
    },
  }).element,
);

// ---------- Forces panel ----------
// Accumulates every distinct force category that has been computed for the current scene
// playthrough. Resets on scene load and on Reset button.
const forcesPanelTitle = document.getElementById('forcesPanelTitle') as HTMLElement;
const forcesPanelBody = document.getElementById('forcesSeenList') as HTMLElement;

function renderForcesPanel() {
  forcesPanelTitle.textContent = i18n.t('panel.forces');
  forcesPanelBody.innerHTML = '';
  if (forcesSeen.size === 0) {
    const empty = document.createElement('span');
    empty.className = 'forces-empty';
    empty.textContent = i18n.t('forces.empty');
    forcesPanelBody.appendChild(empty);
    return;
  }
  for (const key of forcesSeen) {
    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'force-row';
    row.dataset.key = key;
    const hidden = hiddenForces.has(key);
    if (hidden) row.classList.add('hidden');
    const color = FORCE_COLORS[key] ?? '#888';

    const tag = document.createElement('span');
    tag.className = 'force-tag';
    tag.style.borderColor = color;
    tag.style.color = color;
    tag.textContent = i18n.t(key as MechKey);

    const arrow = document.createElement('span');
    arrow.className = 'force-arrow';
    arrow.style.color = color;
    arrow.textContent = '→';

    const name = document.createElement('span');
    name.className = 'force-name';
    name.textContent = i18n.t(forceNameKey(key));

    row.appendChild(tag);
    row.appendChild(arrow);
    row.appendChild(name);

    row.addEventListener('click', () => {
      if (hiddenForces.has(key)) hiddenForces.delete(key);
      else hiddenForces.add(key);
      renderForcesPanel();
    });

    forcesPanelBody.appendChild(row);
  }
}
renderForcesPanel();
i18n.subscribe(() => renderForcesPanel());

// ---------- Action bar ----------
const actionBar = mountActionBar({
  el: document.getElementById('actionBar') as HTMLElement,
  i18n,
  onPlayPause: (p) => {
    playing = p;
  },
  onStep: () => {
    Matter.Engine.update(engine, 16.666);
  },
  onReset: () => {
    trace.points = [];
    forcesSeen.clear();
    hiddenForces.clear();
    rebuildCurrentScene();
    actionBar.setPlaying(SCENES[currentSceneKey].defaultPlaying);
    playing = SCENES[currentSceneKey].defaultPlaying;
  },
  onSpeedChange: (v) => {
    speedScale = v;
  },
});

// ---------- Scene loading ----------
function loadScene() {
  paletteHandle?.destroy();
  paletteHandle = null;
  buildDimControls();
  forcesSeen.clear();
  hiddenForces.clear();
  rebuildCurrentScene();
  playing = SCENES[currentSceneKey].defaultPlaying;
  if (new URL(window.location.href).searchParams.get('paused') === '1') {
    playing = false;
  }
  actionBar.setPlaying(playing);
  if (currentSceneKey === 'sandbox') {
    paletteHandle = mountPalette({
      el: document.getElementById('paletteContainer') as HTMLElement,
      i18n,
      world,
      canvas,
      mouseConstraint,
      // Without this, the body lives in the physics world but never gets drawn,
      // because the renderer only iterates `built.bodies`.
      onSpawn: (body) => {
        built?.bodies.push(body);
      },
    });
    (document.getElementById('palettePanel') as HTMLElement).dataset['visible'] = 'true';
  } else {
    (document.getElementById('palettePanel') as HTMLElement).dataset['visible'] = 'false';
  }
  writeExperimentUrl(DEMO_SLUG, currentSceneKey);
  notifyHostExperiment(currentSceneKey);
}

function rebuildCurrentScene() {
  built?.dispose?.();
  const scene = SCENES[currentSceneKey];
  built = scene.build({ engine, world, dims, bounds: bounds() });
  trace.points = [];
}

// ---------- Forces label (i18n) ----------
function forceLabel(key: string): string {
  return i18n.t(key as MechKey);
}

// ---------- Render loop ----------
function frame() {
  if (playing) {
    Matter.Engine.update(engine, 16.666 * speedScale);
  }
  if (showTrace && currentSceneKey === 'projectile' && built && built.bodies.length >= 2) {
    pushTracePoint(trace, built.bodies[1]!);
  }

  if (built) {
    const state: RenderState = {
      scene: built,
      engine,
      bodyStyle: { fill: bodyColor, stroke: '#222', groundFill: groundColor },
      vectorStyle: {
        pixelsPerNewton: 80,
        labelFont: '11px JetBrains Mono, monospace',
        showLabels: true,
        opacity: vectorOpacity,
      },
      showForces,
      showTrace,
      trace,
      traceColor: '#888',
      selectedBody: grabbedBody(),
      selectionColor: '#d93a3a',
      forceLabel,
      forcesSeen,
      hiddenForces,
    };
    drawWorld(ctx, state);
  }

  if (forcesSeen.size !== lastForcesCount) {
    lastForcesCount = forcesSeen.size;
    renderForcesPanel();
  }

  requestAnimationFrame(frame);
}

loadScene();

// ---------- Screenshot preset (capture pipeline) ----------
function applyShot(id: string): void {
  // Set the scene first, then step the engine a fixed number of ticks so the
  // canvas shows traces / forces / mid-motion bodies rather than the initial frame.
  function setScene(key: SceneKey): void {
    currentSceneKey = key;
    loadScene();
  }
  function step(ticks: number): void {
    for (let i = 0; i < ticks; i++) Matter.Engine.update(engine, 16.666);
  }
  switch (id) {
    case 'incline-mid-slide': {
      setScene('incline');
      step(45);
      break;
    }
    case 'projectile-arc': {
      setScene('projectile');
      showTrace = true;
      trace.points = [];
      step(35);
      break;
    }
    case 'pendulum-swing': {
      setScene('pendulum');
      step(30);
      break;
    }
    case 'spring-mass-osc': {
      setScene('springMass');
      step(20);
      break;
    }
    case 'collisions-impact': {
      setScene('collisions');
      step(25);
      break;
    }
    case 'atwood-pulley': {
      setScene('atwood');
      step(30);
      break;
    }
    default: {
      console.warn(`[shot] unknown preset id: ${id}`);
    }
  }
  playing = false;
  actionBar.setPlaying(false);
}

const shotId = getShotId();
if (shotId !== null) {
  markShotMode();
  applyShot(shotId);
}

frame();

// Drawer wiring lives at the end so containers exist with their final children.
const dimsPanel = document.querySelector('.dims-panel') as HTMLElement;
const palettePanel = document.getElementById('palettePanel') as HTMLElement;
const forcesPanel = document.querySelector('.forces-panel') as HTMLElement;

mountMobileDrawer(dimsPanel, { side: 'left', icon: 'controls' });
mountMobileDrawer([palettePanel, forcesPanel], { side: 'right', icon: 'info' });

makeCollapsible(dimsPanel, {
  initiallyOpen: true,
  storageKey: 'learnforge:collapsible:mechanics:dims',
});
makeCollapsible(palettePanel, {
  initiallyOpen: true,
  storageKey: 'learnforge:collapsible:mechanics:palette',
});
makeCollapsible(forcesPanel, {
  initiallyOpen: true,
  storageKey: 'learnforge:collapsible:mechanics:forces',
});

// ---------- Demo readiness (for visual-regression and screenshot capture) ----------
declare global {
  interface Window {
    __demoReady?: Promise<void>;
  }
}

let resolveDemoReady!: () => void;
window.__demoReady = new Promise<void>((res) => {
  resolveDemoReady = res;
});

// Resolve after two animation frames so the canvas has rendered at least once.
requestAnimationFrame(() => requestAnimationFrame(() => resolveDemoReady()));
